import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useSession } from "@/hooks/use-session";

/**
 * Loads Evidence rows for a list of ids and joins to their parent Paper for
 * the citation block (authors, year, journal, doi). Used by AlertBanner's
 * "Why?" drawer; also by the /evidence list view.
 */

export type EvidenceRow = {
  id: string;
  paper_id: string;
  claim: string;
  paper: {
    id: string;
    handle: string;
    title: string;
    journal: string | null;
    year: number | null;
    doi: string | null;
  } | null;
};

export function useEvidenceByIds(ids: string[]) {
  const { session } = useSession();
  const userId = session?.user?.id;
  const dedupKey = [...new Set(ids)].sort().join(",");

  return useQuery<EvidenceRow[]>({
    queryKey: ["evidence_by_ids", userId, dedupKey],
    enabled: Boolean(userId) && ids.length > 0,
    staleTime: 5 * 60_000,
    queryFn: async () => {
      if (!userId || ids.length === 0) return [];
      const { data, error } = await supabase
        .from("evidence")
        .select(
          "id, paper_id, claim, paper:paper_id (id, handle, title, journal, year, doi)",
        )
        .in("id", ids)
        .eq("user_id", userId);
      if (error) throw error;
      return (data ?? []).map((row) => {
        const r = row as unknown as {
          id: string;
          paper_id: string;
          claim: string;
          paper:
            | { id: string; handle: string; title: string; journal: string | null; year: number | null; doi: string | null }
            | { id: string; handle: string; title: string; journal: string | null; year: number | null; doi: string | null }[]
            | null;
        };
        const paper = Array.isArray(r.paper) ? r.paper[0] ?? null : r.paper;
        return { id: r.id, paper_id: r.paper_id, claim: r.claim, paper };
      });
    },
  });
}
